import { ApplicationConfig, ErrorHandler, importProvidersFrom, isDevMode } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';

import { appRoutes } from './app.routes';
import { AuthModule, AuthHttpInterceptor } from '@auth0/auth0-angular';
import { MarkdownModule } from 'ngx-markdown';

import { environment } from '../environments/environment';
import { authConfig } from './auth/auth.config';
import { ErrorHandlerService } from './error/error-handler';
import { ErrorInterceptorService } from './error/error-interceptor';
import { MessageService } from 'primeng/api';
import { BreadcrumbService } from '@bioconnect-ui/breadcrumb-service';

import { provideAccessControlService } from '@bioconnect-ui/access-control-service';
import { provideCurationService } from '@bioconnect-ui/curation-service';
import { provideDataPackageService } from '@bioconnect-ui/data-package-service';
import { provideFileService } from '@bioconnect-ui/file-service';
import { provideLogService } from '@bioconnect-ui/log-service';
import { provideProtocolService } from '@bioconnect-ui/protocol-service';
import { provideSharedService, provideXlsxService } from '@bioconnect-ui/shared-service';
import { provideSupportService } from '@bioconnect-ui/support-service';
import { provideUserService } from '@bioconnect-ui/user-service';
import { provideAdminService } from '@bioconnect-ui/admin-service';
import { provideFileTypeService } from '@bioconnect-ui/file-type-service';
import { provideResourceService } from '@bioconnect-ui/resource-service';
import { provideContextFilesService } from '@bioconnect-ui/context-files-service';
import { provideBioSampleService } from '@bioconnect-ui/biosample-service';
import { provideMeasureService } from '@bioconnect-ui/measure-service';
import { provideStructuredDataService } from '@bioconnect-ui/structured-data-service';

import { providePrimeNG } from 'primeng/config';
import BioConnectPreset from '../styles/theme/bioconnect-preset'

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(appRoutes),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    importProvidersFrom(
      AuthModule.forRoot({
        domain: authConfig.domain,
        clientId: authConfig.clientId,
        authorizationParams: {
          audience: authConfig.audience,
          redirect_uri: window.location.origin,
        },
        // Persist tokens across reloads in local dev for convenience; use the more secure
        // memory-only cache in real builds, relying on refresh tokens to restore the session.
        cacheLocation: isDevMode() ? 'localstorage' : 'memory',
        useRefreshTokens: true,
        httpInterceptor: {
          allowedList: [`${environment.restServer}/*`, `${environment.structuredDataServer}/*`],
        },
      }),
      MarkdownModule.forRoot(),
    ),
    { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptorService, multi: true },
    { provide: ErrorHandler, useClass: ErrorHandlerService },
    MessageService,
    BreadcrumbService,
    provideAccessControlService({ restServer: environment.restServer }),
    provideCurationService({ restServer: environment.restServer }),
    provideDataPackageService({ restServer: environment.restServer }),
    provideFileService({ restServer: environment.restServer }),
    provideLogService({ instrumentationKey: environment.instrumentationKey, appName: 'portal' }),
    provideProtocolService({ restServer: environment.restServer }),
    provideSharedService({ restServer: environment.restServer }),
    provideXlsxService({ restServer: environment.restServer }),
    provideSupportService({ restServer: environment.restServer }),
    provideUserService({ restServer: environment.restServer }),
    provideAdminService({ restServer: environment.restServer }),
    provideFileTypeService({ restServer: environment.restServer }),
    provideResourceService({ restServer: environment.restServer }),
    provideContextFilesService({ restServer: environment.restServer }),
    provideBioSampleService({ restServer: environment.restServer }),
    provideMeasureService({ restServer: environment.restServer }),
    provideStructuredDataService({ structuredDataServer: environment.structuredDataServer }),
    providePrimeNG({
      theme: { 
        preset: BioConnectPreset, 
        options: { 
          darkModeSelector: '.app-dark-mode', 
          cssLayer: {
            name: 'primeng',
            order: 'tailwind-base, tailwind-theme, base, primeng, tailwind-utilities'
          },
          cssVariables: true,
        } 
      },
      zIndex: { modal: 1100, overlay: 1000, menu: 1000, tooltip: 1100 },
    }),
  ],
};
